import { datadogRum } from "@datadog/browser-rum";

import { isBrowserExtensionError } from "@thalamos/frontend";
import type { ThalamosUser } from "@thalamos/aspire-common";

import { config } from "./config.js";

export function initialiseTracing() {
  if (!config.datadog.disableTracing) {
    // The SDK's defaults are not stable across majors, so every option is set
    // explicitly rather than inherited. Some already match today's default and
    // are pinned anyway, so a future flip cannot change our behaviour silently.
    datadogRum.init({
      applicationId: config.datadog.applicationId,
      clientToken: config.datadog.clientToken,
      site: "datadoghq.eu",
      service: "aspire",
      env: config.environment ?? "unknown",
      version: config.version ?? "unknown",
      trackUserInteractions: false,
      allowedTracingUrls: [config.backendUrl],

      // Enabled, this adds a `baggage` request header that the servers' CORS
      // allowHeaders do not list, so the call fails preflight — costing the
      // request itself, not just its trace. The shipped typings document a
      // `false` default that the runtime does not honour.
      propagateTraceBaggage: false,

      trackResources: true,
      trackLongTasks: true,

      // Deliberately stricter than the SDK default, which masks only user input.
      defaultPrivacyLevel: "mask",

      // An error thrown entirely by a browser extension is not ours to fix.
      beforeSend: (event) => !isBrowserExtensionError(event),
    });
  }
}

export function setUser(user: ThalamosUser | null) {
  if (!config.datadog.disableTracing) {
    if (user) {
      datadogRum.setUser({
        id: user.id,
        name: user.name,
        email: user.email,
      });
    } else {
      datadogRum.clearUser();
    }
  }
}

export function logActionToDatadog(name: string, context?: object) {
  if (!config.datadog.disableTracing) {
    datadogRum.addAction(name, context);
  } else {
    // For local dev, just log to the console
    console.log(name, context);
  }
}

export function logErrorToDatadog(error: Error, context?: object) {
  if (!config.datadog.disableTracing) {
    datadogRum.addError(error, context);
  } else {
    // For local dev, just log to the console
    console.error(error, context);
  }
}
